import React from "react";
import { useSelector } from "react-redux";

/**
 * Reusable wrapper to handle permissions globally.
 * 
 * Usage Examples:
 * 
 * 1. Hide element entirely if unauthorized:
 *    <PermissionWrapper resource="leads" action="show">
 *      <Button>View Details</Button>
 *    </PermissionWrapper>
 * 
 * 2. Show disabled style (greyed out + not-allowed cursor) if unauthorized:
 *    <PermissionWrapper resource="leads" action="update" disabled={true}>
 *      <Button onClick={handleEdit}>Edit Lead</Button>
 *    </PermissionWrapper>
 */
export const PermissionWrapper = ({
  resource,
  action,
  children,
  fallback = null,
  disabled = false,
  bypass = false,
}) => {
  const { currentUser } = useSelector((state) => state.users);

  // Core permissions check helper
  const hasPermission = (res, act) => {
    if (bypass) return true;
    if (currentUser?.role === "Admin") return true;
    if (res === "tasks" || res === "inquiries" || res === "images" || res === "birthday") return true;
    if ((res === "projects" || res.startsWith("projects_")) && (act === "index" || act === "show")) return true;
    if (res === "areas" && act === "index") return true;
    if (!currentUser?.permissions) return false;
    return currentUser.permissions.some(
      (p) => p.resource === res && p.action === act
    );
  };

  const isAllowed = hasPermission(resource, action);

  if (isAllowed) {
    return <>{children}</>;
  }

  // If they do not have permission, but we want to show it as disabled
  if (disabled) {
    const disabledStyle = {
      cursor: "not-allowed",
      opacity: 0.5,
      display: "inline-block",
    };

    const cloneRecursively = (child) => {
      if (!React.isValidElement(child)) return child;

      const extraProps = {
        disabled: true,
        style: { ...(child.props.style || {}), pointerEvents: "none" },
      };

      if (child.props.children) {
        return React.cloneElement(
          child,
          extraProps,
          React.Children.map(child.props.children, cloneRecursively)
        );
      }

      return React.cloneElement(child, extraProps);
    };

    return (
      <span
        className="permission-disabled-wrapper"
        title="You do not have permission to perform this action"
      >
        {React.Children.map(children, cloneRecursively)}
      </span>
    );
  }

  // Otherwise, hide the UI or render the custom fallback component
  return fallback;
};
